import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ErpHomeworkSubmissionDownloadUrlDocument,
  ErpHomeworkSubmissionDownloadUrlQuery,
  ErpHomeworkSubmissionDownloadUrlQueryVariables,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';

interface IHomeworkDownloadUrlHook {
  /** Fetch a fresh presigned URL for the submission's ZIP and open it. */
  openSubmissionFile: (submissionId: string) => Promise<void>;
  /** The submission id currently being resolved (disables its button). */
  pendingId: string | null;
}

/**
 * Spec 0012 — lazy resolver for the short-lived presigned download URL of a
 * ZIP submission (`homeworkSubmissionDownloadUrlForErp`). Always `network-only`
 * via `client.query`: presigned URLs expire in minutes, so a cached one is a
 * broken link. On success the file opens in a new tab; failures surface as a
 * global toast (house pattern — the hook owns the toast, not the component).
 */
export const useHomeworkDownloadUrl = (): IHomeworkDownloadUrlHook => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { addToast } = useToasts();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const openSubmissionFile = useCallback(
    async (submissionId: string) => {
      setPendingId(submissionId);
      try {
        const result = await client.query<
          ErpHomeworkSubmissionDownloadUrlQuery,
          ErpHomeworkSubmissionDownloadUrlQueryVariables
        >({
          query: ErpHomeworkSubmissionDownloadUrlDocument,
          variables: { submissionId },
          fetchPolicy: 'network-only',
        });
        const url = result.data?.homeworkSubmissionDownloadUrlForErp;
        if (url) {
          window.open(url, '_blank', 'noopener');
        } else {
          addToast({
            type: GlobalToastTypeNames.Homework,
            data: {
              type: ToastTypes.error,
              title: t('pages.homeworks.downloadFailed'),
            },
          });
        }
      } catch (e) {
        addToast({
          type: GlobalToastTypeNames.Homework,
          data: {
            type: ToastTypes.error,
            title: t('pages.homeworks.downloadFailed'),
          },
        });
      } finally {
        setPendingId(null);
      }
    },
    [client, addToast, t],
  );

  return { openSubmissionFile, pendingId };
};
